// Copyright (C) 2019-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { connect } from 'react-redux';

import { Row, Col } from 'antd/lib/grid';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import moment from 'moment';

import { getCore, Task, User } from 'cvat-core-wrapper';
import AutomaticAnnotationProgress from 'components/tasks-page/automatic-annotation-progress';
import MdGuideControl from 'components/md-guide/md-guide-control';
import Preview from 'components/common/preview';
import { cancelInferenceAsync } from 'actions/models-actions';
import { CombinedState, ActiveInference } from 'reducers';
import { hasPermissionToChangeTaskAuditor } from 'utils/permissions';
import UserSelector from './user-selector';
// import BugTrackerEditor from './bug-tracker-editor';
import LabelsEditorComponent from '../labels-editor/labels-editor';
import ProjectSubsetField from '../create-task-page/project-subset-field';
import AnnotationLawField from './annotation-law-field';

interface OwnProps {
    task: Task;
    onUpdateTask: (task: Task) => Promise<void>;
}

interface StateToProps {
    activeInference: ActiveInference | null;
    projectSubsets: string[];
    dumpers: any[];
    user: any;
}

interface DispatchToProps {
    cancelAutoAnnotation(): void;
}

function mapStateToProps(state: CombinedState, own: OwnProps): StateToProps & OwnProps {
    const [taskProject] = state.projects.current.filter((project) => project.id === own.task.projectId);

    return {
        ...own,
        dumpers: state.formats.annotationFormats.dumpers,
        user: state.auth.user,
        activeInference: state.models.taskInferences[own.task.id] || null,
        projectSubsets: taskProject ?
            ([
                ...new Set(taskProject.subsets),
            ] as string[]) :
            [],
    };
}

function mapDispatchToProps(dispatch: any, own: OwnProps): DispatchToProps {
    return {
        cancelAutoAnnotation(): void {
            dispatch(cancelInferenceAsync(own.task));
        },
    };
}

const core = getCore();

interface State {
    name: string;
    user: User;
}

type Props = DispatchToProps & StateToProps & OwnProps;

class DetailsComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        const { task: taskInstance, user } = props;
        this.state = {
            name: taskInstance.name,
            user,
        };
    }

    public componentDidUpdate(prevProps: Props): void {
        const { task: taskInstance } = this.props;

        if (prevProps !== this.props) {
            this.setState({
                name: taskInstance.name,
            });
        }
    }

    private renderTaskName(): JSX.Element {
        const { name } = this.state;
        const { task: taskInstance, onUpdateTask } = this.props;

        return (
            <Title level={4}>
                <Text
                    editable={{
                        onChange: (value: string): void => {
                            this.setState({
                                name: value,
                            });

                            taskInstance.name = value;
                            onUpdateTask(taskInstance);
                        },
                    }}
                    className='cvat-text-color'
                >
                    {name}
                </Text>
                <Text strong type='secondary' className='cvat-item-task-id'>{taskInstance.locked ? ' 已被锁定' : ''}</Text>
            </Title>
        );
    }

    private renderDescription(): JSX.Element {
        const { task: taskInstance } = this.props;
        const { owner } = taskInstance;

        return (
            <Row className='cvat-task-details-user-block' justify='space-between' align='middle'>
                <Col span={10}>
                    {owner && (
                        <Text type='secondary'>
                            {`任务 #${taskInstance.id} 由 ${owner} 创建于 ${moment(taskInstance.createdDate).format('YYYY-MM-DD HH:mm:ss')}`}
                        </Text>
                    )}
                </Col>
            </Row>
        );
    }

    private renderUsersAndLaw(): JSX.Element {
        const { task, user, onUpdateTask } = this.props;

        const assigneeSelect = (
            <UserSelector
                value={task.assignee}
                onSelect={(value: User | null): void => {
                    task.assignee = value;
                    onUpdateTask(task);
                }}
                disabled={!user.isSuperuser}
            />
        );

        const auditorSelect = (
            <UserSelector
                value={task.auditor}
                onSelect={(value: User | null): void => {
                    task.auditor = value;
                    onUpdateTask(task);
                }}
                disabled={!hasPermissionToChangeTaskAuditor(user, task)}
            />
        );
        const supervisorSelect = (
            <UserSelector
                value={task.supervisor}
                onSelect={(value: User | null): void => {
                    task.supervisor = value;
                    onUpdateTask(task);
                }}
                disabled={!user.isSuperuser}
            />
        );

        return (
            <>
                <Row className='cvat-task-details-user-block' justify='space-between' align='middle'>
                    <Col span={12}>
                        <Text type='secondary'>管理人员</Text>
                        {assigneeSelect}
                    </Col>
                    <Col span={12}>
                        <Text type='secondary'>审核人员</Text>
                        {auditorSelect}
                    </Col>
                </Row>
                <Row className='cvat-task-details-user-block' justify='space-between' align='middle'>
                    <Col span={12}>
                        <Text type='secondary'>业务人员</Text>
                        {supervisorSelect}
                    </Col>
                    <Col span={12}>
                        <Text type='secondary'>标注规范</Text>
                        <AnnotationLawField
                            value={task.annotationLaw}
                            onChange={(value: any): void => {
                                task.annotationLaw = value;
                                onUpdateTask(task);
                            }}
                            disabled={!user.isSuperuser}
                        />
                    </Col>
                </Row>
            </>
        );
    }

    private renderLabelsEditor(): JSX.Element {
        const { task: taskInstance, onUpdateTask } = this.props;

        return (
            <Row>
                <Col span={24}>
                    <LabelsEditorComponent
                        labels={taskInstance.labels.map((label: any): string => label.toJSON())}
                        onSubmit={(labels: any[]): void => {
                            taskInstance.labels = labels.map((labelData): any => new core.classes.Label(labelData));
                            onUpdateTask(taskInstance);
                        }}
                    />
                </Col>
            </Row>
        );
    }

    private renderFields(): JSX.Element {
        const { user } = this.state;
        const {
            task: taskInstance,
            projectSubsets,
            onUpdateTask,
        } = this.props;

        return (
            <>
                <Row className='cvat-details-component' justify='space-between' align='middle'>
                    <Col span={12}>
                        <Text type='secondary'>标注用途</Text>
                        <ProjectSubsetField
                            value={taskInstance.subset}
                            projectId={taskInstance.projectId as number}
                            projectSubsets={projectSubsets}
                            onChange={(value) => {
                                taskInstance.subset = value;
                                onUpdateTask(taskInstance);
                            }}
                            disabled={!user.isSuperuser}
                        />
                    </Col>
                </Row>
            </>
        );
    }

    public render(): JSX.Element {
        const {
            activeInference,
            task: taskInstance,
            cancelAutoAnnotation,
            // onUpdateTask,
        } = this.props;

        return (
            <div className='cvat-task-details'>
                <Row justify='start' align='middle'>
                    <Col className='cvat-task-details-task-name'>{this.renderTaskName()}</Col>
                </Row>
                <Row justify='space-between' align='top'>
                    <Col md={8} lg={7} xl={7} xxl={6}>
                        <Row justify='start' align='middle'>
                            <Col span={24}>
                                <Preview
                                    task={taskInstance}
                                    loadingClassName='cvat-task-item-loading-preview'
                                    emptyPreviewClassName='cvat-task-item-empty-preview'
                                    previewClassName='cvat-task-item-preview'
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col md={16} lg={17} xl={17} xxl={18}>
                        {this.renderDescription()}
                        { taskInstance.projectId === null && <MdGuideControl instanceType='task' id={taskInstance.id} /> }
                        <Row justify='space-between' align='middle'>
                            {/* <Col span={12}>
                                <BugTrackerEditor
                                    instance={taskInstance}
                                    onChange={(bugTracker) => {
                                        taskInstance.bugTracker = bugTracker;
                                        onUpdateTask(taskInstance);
                                    }}
                                />
                            </Col> */}
                            <Col span={10}>
                                <AutomaticAnnotationProgress
                                    activeInference={activeInference}
                                    cancelAutoAnnotation={cancelAutoAnnotation}
                                />
                            </Col>
                        </Row>
                        {!taskInstance.projectId && this.renderLabelsEditor()}
                        {taskInstance.projectId && this.renderFields()}
                        {this.renderUsersAndLaw()}
                    </Col>
                </Row>
            </div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(DetailsComponent);
